import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { BaseResourceManager } from '../BaseResourceManager';
import { SubdivisionField, ResoResource } from '../../system/reso';
import { useReso } from '../../contexts/ResoContext';
import { ResourceManagerOptions } from '../types';

// Field name mapping for Subdivision
const FIELD_MAPPING: Record<string, string> = {
  [SubdivisionField.KEY]: 'LO_SUBDIVISION_KEY',
  [SubdivisionField.NAME]: 'LO_SUBDIVISION_NAME',
  [SubdivisionField.SYSTEM_VALIDATED]: 'LO_SUBDIVISION_SYSTEM_VALIDATED_FLAG',
  [SubdivisionField.COUNTY]: 'LO_SUBDIVISION_COUNTY',
  [SubdivisionField.STATE]: 'LO_SUBDIVISION_STATE',
  [SubdivisionField.MODIFICATION_TIMESTAMP]:
    'LO_SUBDIVISION_MODIFICATION_TIMESTAMP',
  [SubdivisionField.STATUS]: 'LO_SUBDIVISION_STATUS',
  [SubdivisionField.URL]: 'LO_SUBDIVISION_URL',
  [SubdivisionField.RELATED_KEY]: 'LO_SUBDIVISION_RELATED_SUBDIVISION_KEY',
};

export class SubdivisionResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[FIELD_MAPPING[SubdivisionField.KEY]];
  }

  getItemNumber(): number | undefined {
    return undefined; // Subdivisions don't have an item number
  }

  getListingKeyField(): string | undefined {
    return undefined; // Subdivisions are not directly related to listings
  }

  getListingKey(): string | undefined {
    return undefined; // Subdivisions are not directly related to listings
  }

  // Utility methods for subdivision resources
  getName(): string | undefined {
    return this[FIELD_MAPPING[SubdivisionField.NAME]];
  }

  getStatus(): string | undefined {
    return this[FIELD_MAPPING[SubdivisionField.STATUS]];
  }

  getCounty(): string | undefined {
    return this[FIELD_MAPPING[SubdivisionField.COUNTY]];
  }

  getState(): string | undefined {
    return this[FIELD_MAPPING[SubdivisionField.STATE]];
  }

  isSystemValidated(): boolean | undefined {
    return this[FIELD_MAPPING[SubdivisionField.SYSTEM_VALIDATED]];
  }
}

export class SubdivisionResourceManager extends BaseResourceManager<SubdivisionResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    super(
      ResoResource.SUBDIVISION,
      executeQuery,
      fieldTranslator,
      taskManager,
      {
        ...options,
        useTranslation: false, // Disable translation
      },
    );
  }

  /**
   * Map field name using our custom mapping
   */
  protected mapFieldName(field: string): string {
    return FIELD_MAPPING[field] || field;
  }

  /**
   * Core mapping function from API data to SubdivisionResource
   */
  protected mapFromApiCore(apiData: any): SubdivisionResource {
    return new SubdivisionResource(apiData);
  }

  /**
   * Core mapping function from SubdivisionResource to API data
   */
  protected mapToApiCore(resource: SubdivisionResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Find subdivisions by name
   */
  public async findByName(name: string): Promise<SubdivisionResource[]> {
    return this.findListByField(SubdivisionField.NAME, name);
  }

  /**
   * Find subdivisions by county
   */
  public async findByCounty(county: string): Promise<SubdivisionResource[]> {
    return this.findListByField(SubdivisionField.COUNTY, county);
  }

  /**
   * Find subdivisions by state
   */
  public async findByState(state: string): Promise<SubdivisionResource[]> {
    return this.findListByField(SubdivisionField.STATE, state);
  }

  /**
   * Find validated subdivisions by state and county
   */
  public async findValidatedByStateAndCounty(
    state: string,
    county: string,
  ): Promise<SubdivisionResource[]> {
    return this.findListByFields({
      [SubdivisionField.STATE]: state,
      [SubdivisionField.COUNTY]: county,
      [SubdivisionField.SYSTEM_VALIDATED]: true,
    });
  }

  /**
   * Find subdivisions by state and county
   */
  public async findByStateAndCounty(
    state: string,
    county: string,
  ): Promise<SubdivisionResource[]> {
    return this.findListByFields({
      [SubdivisionField.STATE]: state,
      [SubdivisionField.COUNTY]: county,
    });
  }

  /**
   * Override findListByField to use our field mapping
   */
  public async findListByField(
    field: string,
    value: any,
  ): Promise<SubdivisionResource[]> {
    const mappedField = this.mapFieldName(field);
    return super.findListByField(mappedField, value);
  }

  /**
   * Override findByField to use our field mapping
   */
  public async findByField(
    field: string,
    value: any,
  ): Promise<SubdivisionResource | null> {
    const mappedField = this.mapFieldName(field);
    return super.findByField(mappedField, value);
  }
}

/**
 * Create a new SubdivisionResource instance with required fields
 */
export function createSubdivisionResource(
  data: Partial<Record<string, any>> = {},
): SubdivisionResource {
  const defaults = {};

  return new SubdivisionResource({
    ...defaults,
    ...data,
  });
}
